import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { InsiderBillingHistoryModal } from "@faire/retailer-visitor-shared/components/InsiderBillingHistoryModal/InsiderBillingHistoryModal";
import { useShowBillingHistoryModal } from "@faire/retailer-visitor-shared/components/InsiderBillingHistoryModal/useShowBillingHistoryModal";
import { useInsiderFeatureEligibility } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderFeatureEligibility";
import { useInsiderMembershipPrice } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipPrice";
import { useInsiderSignUpIncentives } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderSignUpIncentives";
import { INSIDER_SIGN_UP_OFFER_DEFAULT_PRICE, INSIDER_SIGN_UP_OFFER_MAXIMUM_AVAILABLE_MONTHS, useInsiderSignUpOffer } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderSignUpOffer";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { useInsiderTrialingPeriod } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderTrialingPeriod";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { SignUpElement, SignUpType, usePopSignUp } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { InsiderMarketingPageCTA } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/__internal__/InsiderMarketingPageCTA";
import { RejoinInsiderMsg } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/consts";
import { useFormatMoney } from "@faire/web--source/common/money";
import { Path } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { LoadingSkeleton } from "@faire/web--source/slate/LoadingSkeleton";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Money } from "@faire/web--source/ui/Money";
import { Color as ColorSpan, Medium } from "@faire/web--source/ui/Typography/Span";
import { useQuery as useInsiderMembershipInvoicesQuery } from "@faire/web-api--source/api/membership/membership-invoices/get-hooks";
import { useQuery as useIsEligibleForAnnualBillingQuery } from "@faire/web-api--source/api/retailer/insider/is-eligible-for-annual-billing/get-hooks";
import { trackInsiderLoggedOutMarketingPageHeroBannerCtaClick } from "@faire/web-api--source/events/insider/click/loggedOutMarketingPageHeroBannerCta";
import { trackInsiderUkProfitabilityMarketingPageHeroBannerClick } from "@faire/web-api--source/events/insider/click/ukProfitabilityMarketingPageHeroBanner";
import { trackInsiderLoggedOutMarketingPageHeroBannerView } from "@faire/web-api--source/events/insider/view/loggedOutMarketingPageHeroBanner";
import { IInsiderModalRequest } from "@faire/web-api--source/indigofair/insider/IInsiderModalRequest";
import insiderInfo from "@faire/web-api--source/routes/user/insider/info";
import React from "react";
import styled from "styled-components";
export var HeroBanner = function() {
    var isInsiderCancelled = useInsiderState().isInsiderCancelled;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var ref = useDoActionWhenVisible(function() {
        trackInsiderLoggedOutMarketingPageHeroBannerView();
    });
    var _useIsEligibleForAnnualBillingQuery = useIsEligibleForAnnualBillingQuery({
        enabled: isLoggedInRetailer
    }), data = _useIsEligibleForAnnualBillingQuery.data, isLoading = _useIsEligibleForAnnualBillingQuery.isLoading;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {});
    }
    return /*#__PURE__*/ _jsxs(OuterWrapper, {
        ref: ref,
        children: [
            /*#__PURE__*/ _jsx(HalfBanner, {
                children: /*#__PURE__*/ _jsxs(ContentWrapper, {
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            align: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Insider, {
                                    fontSize: "24px",
                                    color: Core.icon.inverse
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    width: "1x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    as: "p",
                                    variant: "subheadingSansMedium",
                                    color: Core.text.inverse,
                                    "data-test-id": "insider-hero-title",
                                    children: "Insider"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "1x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: {
                                mobileAndAbove: "displayMSerifRegular",
                                desktopAndAbove: "displayLSerifRegular"
                            },
                            color: Core.text.inverse,
                            as: "h1",
                            children: !isLoggedInRetailer ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "TtJFa7",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Free shipping with thousands of brands"
                                    }
                                ]
                            }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "WtN/hM",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "The best way to shop on Faire"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "2x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            color: Core.text.inverse,
                            children: /*#__PURE__*/ _jsx(SubheadingContent, {
                                isEligibleForAnnualBilling: data === null || data === void 0 ? void 0 : data.is_eligible_for_annual_billing
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "4x"
                        }),
                        /*#__PURE__*/ _jsxs(Flex, {
                            gap: "2x",
                            direction: {
                                mobileAndAbove: "column",
                                tabletAndAbove: "row"
                            },
                            align: {
                                mobileAndAbove: "flex-start",
                                tabletAndAbove: "center"
                            },
                            children: [
                                /*#__PURE__*/ _jsx(InsiderMarketingPageCTA, {
                                    cta: /*#__PURE__*/ _jsx(HeroBannerCTA, {}),
                                    hideTooltip: !isLoggedInRetailer
                                }),
                                /*#__PURE__*/ _jsx(InsiderMembershipInvoicesCTA, {})
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "1x"
                        }),
                        isLoggedInRetailer ? /*#__PURE__*/ _jsx(BannerCaption, {
                            hasChurned: isInsiderCancelled,
                            isEligibleForAnnualBilling: !!(data === null || data === void 0 ? void 0 : data.is_eligible_for_annual_billing)
                        }) : null
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(HalfBanner, {
                children: /*#__PURE__*/ _jsx(ImageContainer, {
                    children: /*#__PURE__*/ _jsx(Image, {})
                })
            })
        ]
    });
};
var HeroBannerCTA = function() {
    var isInsiderCancelled = useInsiderState().isInsiderCancelled;
    var userCanPlaceOrder = useUserStore([
        "userCanPlaceOrder"
    ]).userCanPlaceOrder;
    var disableCTA = !userCanPlaceOrder;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var popSignUp = usePopSignUp().popSignUp;
    var handleSignUp = function() {
        return popSignUp(SignUpType.GENERIC, trackInsiderLoggedOutMarketingPageHeroBannerCtaClick, {
            element: SignUpElement.HERO,
            sourceId: "insider_hero_cta"
        });
    };
    if (!isLoggedInRetailer) {
        return /*#__PURE__*/ _jsx(StyledButton, {
            variant: "secondary",
            onClick: handleSignUp,
            "data-test-id": "insider-hero-enroll-cta",
            children: /*#__PURE__*/ _jsx(Column, {
                children: /*#__PURE__*/ _jsx(CtaText, {})
            })
        });
    }
    return /*#__PURE__*/ _jsx(StyledButton, {
        variant: "secondary",
        disabled: disableCTA,
        linkTo: new Path({
            pathname: insiderInfo(),
            search: ""
        }).append({
            insiderEnrolment: IInsiderModalRequest.InsiderModalContext.MARKETING_PAGE
        }).toString(),
        onClick: trackInsiderUkProfitabilityMarketingPageHeroBannerClick,
        "data-test-id": "insiderMarketingPageEnrollmentCta",
        children: /*#__PURE__*/ _jsx(Column, {
            children: /*#__PURE__*/ _jsx(CtaText, {
                hasChurned: isInsiderCancelled
            })
        })
    });
};
var InsiderMembershipInvoicesCTA = function() {
    var _data_insider_membership_invoices;
    var _useShowBillingHistoryModal = useShowBillingHistoryModal(), showBillingHistoryModal = _useShowBillingHistoryModal.showBillingHistoryModal, toggleModal = _useShowBillingHistoryModal.toggleModal;
    var isInsiderCancelled = useInsiderState().isInsiderCancelled;
    var _useInsiderMembershipInvoicesQuery = useInsiderMembershipInvoicesQuery({
        enabled: isInsiderCancelled
    }), isFetching = _useInsiderMembershipInvoicesQuery.isFetching, data = _useInsiderMembershipInvoicesQuery.data;
    if (!isInsiderCancelled) {
        return null;
    }
    if (isFetching) {
        return /*#__PURE__*/ _jsx(Button, {
            variant: "tertiary",
            loading: true
        });
    }
    return (data === null || data === void 0 ? void 0 : (_data_insider_membership_invoices = data.insider_membership_invoices) === null || _data_insider_membership_invoices === void 0 ? void 0 : _data_insider_membership_invoices.length) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                variant: "tertiary",
                onClick: toggleModal,
                tertiaryColor: Core.text.inverse,
                minWidth: "150px",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "uuzF60",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "View billing history"
                        }
                    ]
                })
            }),
            showBillingHistoryModal ? /*#__PURE__*/ _jsx(InsiderBillingHistoryModal, {
                onClose: toggleModal
            }) : null
        ]
    }) : null;
};
var SubheadingContent = function(param) {
    var isEligibleForAnnualBilling = param.isEligibleForAnnualBilling;
    var hasFreeDutyAdvertising = useInsiderFeatureEligibility().hasFreeDutyAdvertising;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    if (!isLoggedInRetailer) {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "bDjxcU",
            defaultMessage: [
                {
                    type: 0,
                    value: "As an Insider, enjoy access to free shipping across Faire. Sign up today and get a free trial of Insider."
                }
            ]
        });
    }
    if (isEligibleForAnnualBilling) {
        return hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "cp4SOi",
            defaultMessage: [
                {
                    type: 0,
                    value: "Unlock free shipping and import duties on thousands of brands when you join our membership program."
                }
            ]
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "F42hjW",
            defaultMessage: [
                {
                    type: 0,
                    value: "Unlock free shipping on thousands of brands when you join our membership program."
                }
            ]
        });
    }
    return hasFreeDutyAdvertising ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "fep/WB",
        defaultMessage: [
            {
                type: 0,
                value: "Unlock free shipping and import duties on thousands of brands when you join our monthly membership program."
            }
        ]
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "xlEtKs",
        defaultMessage: [
            {
                type: 0,
                value: "Unlock free shipping on thousands of brands when you join our monthly membership program."
            }
        ]
    });
};
var CtaText = function(param) {
    var hasChurned = param.hasChurned;
    var _useInsiderTrialingPeriod = useInsiderTrialingPeriod(), trialingPeriodMonths = _useInsiderTrialingPeriod.trialingPeriodMonths, trialingPeriodDays = _useInsiderTrialingPeriod.trialingPeriodDays;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    if (!isLoggedInRetailer) {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "g7hd3b",
            defaultMessage: [
                {
                    type: 0,
                    value: "Sign up to shop"
                }
            ]
        });
    }
    if (!hasChurned) {
        if (trialingPeriodMonths > 1) {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "ClrO9o",
                defaultMessage: [
                    {
                        type: 6,
                        value: "trialMonthsNumber",
                        options: {
                            one: {
                                value: [
                                    {
                                        type: 0,
                                        value: "Try Insider, free for "
                                    },
                                    {
                                        type: 7
                                    },
                                    {
                                        type: 0,
                                        value: " month"
                                    }
                                ]
                            },
                            other: {
                                value: [
                                    {
                                        type: 0,
                                        value: "Try Insider, free for "
                                    },
                                    {
                                        type: 7
                                    },
                                    {
                                        type: 0,
                                        value: " months"
                                    }
                                ]
                            }
                        },
                        offset: 0,
                        pluralType: "cardinal"
                    }
                ],
                values: {
                    trialMonthsNumber: trialingPeriodMonths
                }
            });
        }
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "DYT2pb",
            defaultMessage: [
                {
                    type: 6,
                    value: "trialPeriodInDaysNumber",
                    options: {
                        one: {
                            value: [
                                {
                                    type: 0,
                                    value: "Try Insider, free for "
                                },
                                {
                                    type: 7
                                },
                                {
                                    type: 0,
                                    value: " day"
                                }
                            ]
                        },
                        other: {
                            value: [
                                {
                                    type: 0,
                                    value: "Try Insider, free for "
                                },
                                {
                                    type: 7
                                },
                                {
                                    type: 0,
                                    value: " days"
                                }
                            ]
                        }
                    },
                    offset: 0,
                    pluralType: "cardinal"
                }
            ],
            values: {
                trialPeriodInDaysNumber: trialingPeriodDays
            }
        });
    }
    return /*#__PURE__*/ _jsx(RejoinInsiderMsg, {});
};
var AnnualBillingBannerCaption = function(param) {
    var monthlyAnnualOfferPrice = param.monthlyAnnualOfferPrice;
    var formatMoney = useFormatMoney().formatMoney;
    var insiderAnnualMembershipMonthlyPrice = useInsiderMembershipPrice().insiderAnnualMembershipMonthlyPrice;
    var formattedAnnualMembershipMonthlyPrice = formatMoney({
        money: insiderAnnualMembershipMonthlyPrice
    });
    if (monthlyAnnualOfferPrice) {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "xtyMzG",
            defaultMessage: [
                {
                    type: 0,
                    value: "Starting at "
                },
                {
                    type: 1,
                    value: "originalMembershipFeeMoney"
                },
                {
                    type: 0,
                    value: " "
                },
                {
                    type: 8,
                    value: "DiscountedValueTextFormatting",
                    children: [
                        {
                            type: 1,
                            value: "discountedMembershipFeeMoney"
                        },
                        {
                            type: 0,
                            value: "/month"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " after your free trial (when you select annual plan)."
                }
            ],
            values: {
                originalMembershipFeeMoney: /*#__PURE__*/ _jsx(StrikeThroughText, {
                    children: formattedAnnualMembershipMonthlyPrice
                }),
                discountedMembershipFeeMoney: /*#__PURE__*/ _jsx(Money, {
                    money: monthlyAnnualOfferPrice
                }),
                DiscountedValueTextFormatting: DiscountedValueTextFormatting
            }
        });
    }
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "/yBqL/",
        defaultMessage: [
            {
                type: 0,
                value: "Starting at "
            },
            {
                type: 1,
                value: "originalMembershipFeeMoney"
            },
            {
                type: 0,
                value: "/month (when you select annual plan). Cancel anytime."
            }
        ],
        values: {
            originalMembershipFeeMoney: formattedAnnualMembershipMonthlyPrice
        }
    });
};
var BannerCaption = function(param) {
    var hasChurned = param.hasChurned, isEligibleForAnnualBilling = param.isEligibleForAnnualBilling;
    var formatMoney = useFormatMoney().formatMoney;
    var _useInsiderSignUpIncentives = useInsiderSignUpIncentives(isEligibleForAnnualBilling), monthlyAnnualOfferPrice = _useInsiderSignUpIncentives.monthlyAnnualOfferPrice, isInsiderSignUpIncentivesFetching = _useInsiderSignUpIncentives.isInsiderSignUpIncentivesFetching;
    var shouldShowInsiderSignupIncentive = useRetailerStore([
        "shouldShowInsiderSignupIncentive"
    ]).shouldShowInsiderSignupIncentive;
    var _useInsiderMembershipPrice = useInsiderMembershipPrice(), insiderMembershipIncomingPrice = _useInsiderMembershipPrice.insiderMembershipIncomingPrice, insiderMembershipStandardPrice = _useInsiderMembershipPrice.insiderMembershipStandardPrice;
    var formattedMonthlyPrice = formatMoney({
        money: insiderMembershipIncomingPrice
    });
    var formattedDefaultMonthlyPrice = formatMoney({
        money: insiderMembershipStandardPrice
    });
    var insiderSignUpOffer = useInsiderSignUpOffer().insiderSignUpOffer;
    var _insiderSignUpOffer_membership_price;
    var insiderSignUpOfferPrice = (_insiderSignUpOffer_membership_price = insiderSignUpOffer === null || insiderSignUpOffer === void 0 ? void 0 : insiderSignUpOffer.membership_price) !== null && _insiderSignUpOffer_membership_price !== void 0 ? _insiderSignUpOffer_membership_price : INSIDER_SIGN_UP_OFFER_DEFAULT_PRICE;
    if (isInsiderSignUpIncentivesFetching) {
        return /*#__PURE__*/ _jsx(StyledLoadingSkeleton, {});
    }
    if (isEligibleForAnnualBilling) {
        return /*#__PURE__*/ _jsx(Disclaimer, {
            $hasDesktopMaxWidth: true,
            children: /*#__PURE__*/ _jsx(AnnualBillingBannerCaption, {
                monthlyAnnualOfferPrice: monthlyAnnualOfferPrice
            })
        });
    }
    if (shouldShowInsiderSignupIncentive) {
        return /*#__PURE__*/ _jsx(Disclaimer, {
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "4YCdAj",
                defaultMessage: [
                    {
                        type: 6,
                        value: "durationMonthsNumber",
                        options: {
                            one: {
                                value: [
                                    {
                                        type: 0,
                                        value: "Only "
                                    },
                                    {
                                        type: 1,
                                        value: "originalMembershipFeeMoney"
                                    },
                                    {
                                        type: 0,
                                        value: " "
                                    },
                                    {
                                        type: 8,
                                        value: "DiscountedValueTextFormatting",
                                        children: [
                                            {
                                                type: 1,
                                                value: "discountedMembershipFeeMoney"
                                            },
                                            {
                                                type: 0,
                                                value: "/month"
                                            }
                                        ]
                                    },
                                    {
                                        type: 0,
                                        value: " for "
                                    },
                                    {
                                        type: 7
                                    },
                                    {
                                        type: 0,
                                        value: " month after your free trial. Cancel anytime."
                                    }
                                ]
                            },
                            other: {
                                value: [
                                    {
                                        type: 0,
                                        value: "Only "
                                    },
                                    {
                                        type: 1,
                                        value: "originalMembershipFeeMoney"
                                    },
                                    {
                                        type: 0,
                                        value: " "
                                    },
                                    {
                                        type: 8,
                                        value: "DiscountedValueTextFormatting",
                                        children: [
                                            {
                                                type: 1,
                                                value: "discountedMembershipFeeMoney"
                                            },
                                            {
                                                type: 0,
                                                value: "/month"
                                            }
                                        ]
                                    },
                                    {
                                        type: 0,
                                        value: " for "
                                    },
                                    {
                                        type: 7
                                    },
                                    {
                                        type: 0,
                                        value: " months after your free trial. Cancel anytime."
                                    }
                                ]
                            }
                        },
                        offset: 0,
                        pluralType: "cardinal"
                    }
                ],
                values: {
                    originalMembershipFeeMoney: /*#__PURE__*/ _jsx(StrikeThroughText, {
                        children: formattedDefaultMonthlyPrice
                    }),
                    discountedMembershipFeeMoney: /*#__PURE__*/ _jsx(Money, {
                        money: insiderSignUpOfferPrice
                    }),
                    DiscountedValueTextFormatting: DiscountedValueTextFormatting,
                    durationMonthsNumber: INSIDER_SIGN_UP_OFFER_MAXIMUM_AVAILABLE_MONTHS
                }
            })
        });
    }
    return hasChurned ? /*#__PURE__*/ _jsx(Disclaimer, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "VuNEbk",
            defaultMessage: [
                {
                    type: 0,
                    value: "Only "
                },
                {
                    type: 1,
                    value: "monthlyMembershipFeeMoney"
                },
                {
                    type: 0,
                    value: "/month. Cancel anytime."
                }
            ],
            values: {
                monthlyMembershipFeeMoney: formattedMonthlyPrice
            }
        })
    }) : /*#__PURE__*/ _jsx(Disclaimer, {
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "15PT1h",
            defaultMessage: [
                {
                    type: 0,
                    value: "After your free trial, only "
                },
                {
                    type: 1,
                    value: "monthlyMembershipFeeMoney"
                },
                {
                    type: 0,
                    value: "/month. Cancel anytime."
                }
            ],
            values: {
                monthlyMembershipFeeMoney: formattedMonthlyPrice
            }
        })
    });
};
var IMAGE_URL_MOBILE_AND_TABLET = "https://cdn.faire.com/fastly/f3457b4214d3d2246305e75ca2bc12691a19de404d497229208a171ddf35eb4b.jpeg";
var IMAGE_URL_DESKTOP = "https://cdn.faire.com/fastly/68beae013789597b98a6af24dc63a94f5e050faa3c26ae2e08878127a5fad8ba.jpeg";
var OuterWrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column-reverse",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-a3cd10d7-0"
})([
    "position:relative;width:100%;background-color:",
    ";"
], Decorative.shade.teal);
var HalfBanner = styled(Column).withConfig({
    componentId: "sc-a3cd10d7-1"
})([
    "width:100%;",
    "{width:50%;}"
], responsiveMediaQueries.tabletAndAbove);
var ContentWrapper = styled(Column).attrs({
    justify: {
        mobileAndAbove: "normal",
        tabletAndAbove: "center"
    }
}).withConfig({
    componentId: "sc-a3cd10d7-2"
})([
    "align-self:center;padding:",
    ";",
    "{max-width:438px;height:424px;padding:",
    ";}",
    "{max-width:538px;height:530px;padding:",
    ";}",
    "{max-width:638px;padding:",
    ";}",
    "{max-width:unset;padding:",
    ";}"
], getSpacing("5x", "2x"), responsiveMediaQueries.tabletAndAbove, getSpacing("5x", "7x"), responsiveMediaQueries.desktopAndAbove, getSpacing("7x"), responsiveMediaQueries.xLargeAndAbove, getSpacing("10x", 112), responsiveMediaQueries.xxlarge, getSpacing(112, 168));
var ImageContainer = styled.div.withConfig({
    componentId: "sc-a3cd10d7-3"
})([
    "width:100%;overflow:visible;order:1;",
    "{width:50%;order:unset;}"
], responsiveMediaQueries.tabletAndAbove);
var Image = styled.div.withConfig({
    componentId: "sc-a3cd10d7-4"
})([
    "position:relative;width:100%;height:200px;background-image:url(",
    ");background-repeat:no-repeat;background-size:cover;background-position:left 50%;",
    "{width:50vw;height:424px;background-position:left top;background-image:url(",
    ");}",
    "{height:530px;background-image:url(",
    ");}"
], IMAGE_URL_MOBILE_AND_TABLET, responsiveMediaQueries.tabletAndAbove, IMAGE_URL_MOBILE_AND_TABLET, responsiveMediaQueries.desktopAndAbove, IMAGE_URL_DESKTOP);
var Disclaimer = styled(Typography).attrs({
    variant: "labelSansRegular",
    color: Core.text.inverse
}).withConfig({
    componentId: "sc-a3cd10d7-5"
})([
    "max-width:226px;",
    "{max-width:",
    ";}"
], responsiveMediaQueries.tabletAndAbove, function(param) {
    var $hasDesktopMaxWidth = param.$hasDesktopMaxWidth;
    return $hasDesktopMaxWidth ? "325px" : "none";
});
var StyledButton = styled(Button).withConfig({
    componentId: "sc-a3cd10d7-6"
})([
    "min-width:256px;width:fit-content;",
    "{min-width:288px;}"
], responsiveMediaQueries.desktopAndAbove);
var StrikeThroughText = styled.span.withConfig({
    componentId: "sc-a3cd10d7-7"
})([
    "text-decoration:line-through;"
]);
var DiscountedValueTextFormatting = function(chunk) {
    return /*#__PURE__*/ _jsx(ColorSpan, {
        color: Core.text.inverse,
        children: /*#__PURE__*/ _jsx(Medium, {
            children: chunk
        })
    });
};
var StyledLoadingSkeleton = styled(LoadingSkeleton).attrs({
    width: "305px",
    height: "16px",
    borderRadius: "4px"
}).withConfig({
    componentId: "sc-a3cd10d7-8"
})([
    "background-color:",
    ";"
], Decorative.icon.teal);
